/* eslint-disable no-console */
import React, {Fragment, useEffect, useRef, useState} from 'react';
import {
  ResponsiveContainer,
  BarChart as BarRe,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  Rectangle,
  Cell
} from 'recharts';

import {formatNumber} from '../../../pages/netZero/utils/commonFunctions';
import {sortBy} from '../../../shared/commonFunctions';
// import {splitSentenceIntoTwo} from '../../../utils/commonFunctions';

import BarChartTooltip from './utils/BarChartTooltip';
import {
  divideIntoSixParts,
  getIntensityYAxisTicks,
  INTENSITY_Y_AXIS_MAX
} from './utils/commonFunctions';

const TCO2E_TO_KT_THRESHOLD = 999.99;

const formatIntensityTickValue = (value: number): string =>
  value === 0 ? '0' : value.toFixed(6).replace(/\.?0+$/, '');

const formatIntensityDecimalTickValue = (value: number): string => {
  if (value === 0) {
    return '0';
  }
  if (Math.abs(value) < 0.0001) {
    return value.toExponential(2);
  }

  return value.toFixed(4).replace(/\.?0+$/, '');
};

const getIntensityRawMax = (chartData: any[]): number => {
  const numericValues = (chartData ?? [])
    .map((item) => item?.value)
    .filter(
      (value): value is number =>
        typeof value === 'number' && Number.isFinite(value)
    );

  return numericValues.length ? Math.max(...numericValues) : 0;
};

const buildIntensityTicks = (axisMax: number, steps: number = 3): number[] => {
  const ticks: number[] = [];
  const increment = axisMax / steps;
  const precision = axisMax < 0.01 ? 6 : axisMax < 1 ? 4 : 2;

  for (let i = 0; i <= steps; i++) {
    ticks.push(Number((i * increment).toFixed(precision)));
  }

  return ticks;
};

// import {convertPxToRem} from '../../../utils/commonFunctions';

// const CustomizedXAxisTick: React.FC<{
//   x: number;
//   y: number;
//   payload: any;
//   rotate?: boolean;
// }> = ({x, y, payload, rotate = false}) => {
//   const label = rotate ? payload?.value : splitSentenceIntoTwo(payload?.value);

//   return (
//     <g transform={`translate(${x},${y})`} key={payload?.value}>
//       {rotate ? (
//         <text
//           x={0}
//           y={0}
//           dy={rotate ? 5 : 16}
//           textAnchor="start"
//           transform="rotate(90)"
//           style={{font: 'normal normal 400 12px/15px Montserrat', fill: '#666'}}
//         >
//           {payload?.value}
//         </text>
//       ) : (
//         <text
//           x={0}
//           y={0}
//           dy={16}
//           fill="#666"
//           style={{font: 'normal normal 400 12px/15px Montserrat'}}
//         >
//           {label?.map((item: any, index: number) =>
//             item ? (
//               <tspan
//                 textAnchor="middle"
//                 x="0"
//                 dy={index === 0 ? '15' : '16'}
//                 key={index}
//               >
//                 {item}
//               </tspan>
//             ) : (
//               <Fragment key={index}></Fragment>
//             )
//           )}
//         </text>
//       )}
//     </g>
//   );
// };

const OverviewBarChart: React.FC<{
  data: any[];
  isColorReverse?: boolean;
  mainData?: any;
  isIntensityChart?: boolean;
}> = ({data, mainData, isColorReverse = false, isIntensityChart = false}) => {
  const isAllZero = data?.every(
    (item: any) => item?.value === 0 || typeof item?.value !== 'number'
  );

  if (isAllZero) {
    data = [];
  }
  const usePerBarFill =
    Boolean(data?.length) && data.every((item: any) => item?.fill != null);
  const chartData = usePerBarFill ? data : sortBy(data, 'value', 'dec');
  const [tooltipPosition, setTooltipPosition] = useState({
    x: 0,
    y: 0,
    barWidth: 0,
    show: false
  });
  const tooltipOffset = 30;
  const [width, setWidth] = useState(0);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (tooltipRef.current) {
      const {offsetWidth} = tooltipRef.current;
      const totalWidth = tooltipPosition.barWidth - offsetWidth;
      setWidth(totalWidth / 2);
    } else {
      setWidth(0);
    }
  }, [tooltipPosition]);

  const useFixedIntensityScale =
    isIntensityChart &&
    Boolean(data?.length) &&
    data.every(
      (item: any) =>
        typeof item?.value === 'number' && item.value < INTENSITY_Y_AXIS_MAX
    );

  const rawMax = isIntensityChart ? getIntensityRawMax(data) : 0;
  const values = data?.length ? data.map((item) => Math.ceil(item.value)) : [];
  const dynamicMax = isIntensityChart
    ? rawMax < 1
      ? rawMax
      : Math.ceil(rawMax)
    : values.length
      ? Math.max(...values)
      : 100;
  const unit = data?.[0]?.unit ?? '';
  const useKScale =
    !isIntensityChart && unit === 'tCO2e' && dynamicMax > TCO2E_TO_KT_THRESHOLD;
  const useIntensityDecimalScale =
    isIntensityChart && !useFixedIntensityScale && !isAllZero && rawMax < 1;

  let max: number;
  let divideSix: number[];

  if (isAllZero) {
    max = 100;
    divideSix = divideIntoSixParts(100, 3);
  } else if (useFixedIntensityScale) {
    max = INTENSITY_Y_AXIS_MAX;
    divideSix = getIntensityYAxisTicks(INTENSITY_Y_AXIS_MAX, 3);
  } else if (isIntensityChart) {
    max = rawMax < 1 ? rawMax * 1.1 : Math.ceil(rawMax) * 1.1;
    divideSix = buildIntensityTicks(max, 3);
  } else {
    max = dynamicMax;
    divideSix = divideIntoSixParts(dynamicMax * 1.1, 3);
  }
  const divideIntoSixFormat = divideSix.map((item) => {
    if (useFixedIntensityScale) {
      return `${formatIntensityTickValue(item)} ${unit}`.length;
    }
    if (useIntensityDecimalScale) {
      return `${formatIntensityDecimalTickValue(item)} ${unit}`.length;
    }
    if (useKScale) {
      return `${(item / 1000).toFixed(2)} KtCO2e`.length;
    }

    return `${formatNumber(item)} ${unit}`.length;
  });
  const showChart = Number.isFinite(max) && max >= 0;
  const color = mainData?.fill ?? '#1A79FF';

  const yAxisTickFormatter = useFixedIntensityScale
    ? (value: number) => `${formatIntensityTickValue(value)} ${unit}`
    : useIntensityDecimalScale
      ? (value: number) => `${formatIntensityDecimalTickValue(value)} ${unit}`
      : useKScale
        ? (value: number) => `${(value / 1000).toFixed(2)} KtCO2e`
        : (value: number) => `${formatNumber(value)} ${unit}`;

  const colorHex = isColorReverse
    ? {tooltipBg: '#5697F8', barColor: '#F76D7B'}
    : {tooltipBg: '#FF3366', barColor: color};

  return showChart ? (
    <ResponsiveContainer className="dontContain" width="100%" height="100%">
      <BarRe data={chartData}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="source"
          axisLine={false}
          tickLine={false}
          // tick={(props) => (
          //   <CustomizedXAxisTick
          //     {...props}
          //   />
          // )}
          tick={false}
          includeHidden
          interval={0}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          domain={showChart ? [0, max] : undefined}
          ticks={divideSix}
          style={{font: 'normal normal 400 12px/15px Montserrat'}}
          tickFormatter={yAxisTickFormatter}
          width={Math.max(...divideIntoSixFormat) * 7.5}
        />
        <Tooltip
          cursor={false}
          position={{
            x: tooltipPosition.x + width,
            y: tooltipPosition.y
          }}
          content={({payload}: any) => {
            return (
              <BarChartTooltip
                active={tooltipPosition.show}
                payload={payload}
                tooltipRef={tooltipRef}
                bgColor={'#FFFFFF'}
              />
            );
          }}
          isAnimationActive={false}
        />
        <Bar
          style={{width: '100%', height: '100%'}}
          dataKey="value"
          radius={usePerBarFill ? [4, 4, 0, 0] : undefined}
          shape={
            usePerBarFill ? undefined : (
              <Rectangle fill={colorHex.barColor} radius={10} />
            )
          }
          barSize={usePerBarFill ? 28 : data?.length <= 3 ? 50 : 20}
          minPointSize={(barValue: number) => {
            return barValue === 0 ? 0 : 3;
          }}
          onMouseEnter={(e) => {
            setTooltipPosition({
              x: e.x,
              y: e.y - tooltipOffset,
              barWidth: e.width,
              show: true
            });
          }}
          onMouseOut={(e) => {
            setTooltipPosition({
              x: e.x,
              y: e.y - tooltipOffset,
              barWidth: e.width,
              show: false
            });
          }}
        >
          {usePerBarFill &&
            chartData.map((entry: any, index: number) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
        </Bar>
      </BarRe>
    </ResponsiveContainer>
  ) : (
    <></>
  );
};

export default OverviewBarChart;
